import { useCallback, useMemo } from 'react'
import { crossSectionIsSteel } from '@domainUtils'
import { filter } from 'lodash-es'
import { Vector3 } from 'three'
import shallow from 'zustand/shallow'
import { useTheme } from '@mui/material'
import { ImmutableVector3, LineShapesMesh, toImmutable, toVector3 } from '@modugen/ui-toolkit'
import { OpacityOption } from '@modugen/ui-toolkit/dist/scene/types/opacity'
import { ThreeEvent } from '@react-three/fiber'
import { useTriggerContextMenu } from '@ui/hooks'
import { useAppStore } from '@stores'
import { useModelStore, useSceneStore } from '@editorStores'
import {
  useGuidToCrossSection,
  useMouseMovedSinceMouseDown,
  usePositionVisibilityLookup,
} from '@editorHooks'
import MemberMeshElement from '../../MemberMeshElement'
import SimpleCylinderMesh from '../../SimpleCylinderMesh'
import useEmitModelClickListenerV2 from '../hooks/useEmitModelClickListenerV2'
import { useModelMeshStore } from '../state/store'

const getPositionVectorFromCheckPosition = (
  checkPosition: number,
  start: Vector3,
  end: Vector3,
  type: 'rip' | 'lintel',
) => {
  const length = start.distanceTo(end)
  const absolutePosition = length * checkPosition

  // Rips only go in the z direction
  if (type === 'rip') return new Vector3(start.x, start.y, start.z + absolutePosition)

  // Lintels can go in x or y direction depending on orientation of the wall
  if (start.x !== end.x) return new Vector3(start.x + absolutePosition, start.y, start.z)
  else return new Vector3(start.x, start.y + absolutePosition, start.z)
}

const DEFAULT_CROSS_SECTION_SIZE = 0.15

interface LineShapesRenderMode {
  bodyGeometry: boolean
  outline: boolean
}

const PositionsMesh = () => {
  // TODO: pass by reference
  const lintels = useModelStore(state => state.model.lintels)
  const rips = useModelStore(state => state.model.rips)
  const persistedRips = useMemo(() => filter(rips, rip => !rip.is_local), [rips])

  const guidToCrossSection = useGuidToCrossSection()
  const translucent = useModelStore(state => state.translucent)
  const { visibilityByType: transparencyVisibilityByType, guidToTransparent } = useModelStore(
    state => state.transparencyVisibilityModel,
    shallow,
  )
  const transparentOpacity = useAppStore(state => state.transparentOpacityV2 ?? 0.1)

  // Define opacity option values: 0.1 for transparent, 1 for notTransparent
  const opacityOptionValues = useMemo<Record<OpacityOption, number>>(
    () => ({
      transparent: transparentOpacity,
      notTransparent: 1,
    }),
    [transparentOpacity],
  )

  const guidToOpacity = useMemo<Record<string, OpacityOption>>(() => {
    return [...lintels, ...persistedRips].reduce((acc, element) => {
      const guid = element.position_guid
      // Determine element type
      const elementType = element.element_type === 'rips' ? 'rips' : 'lintels'

      if (!translucent) {
        acc[guid] = 'notTransparent'
        return acc
      }

      // Priority: guidToTransparent has higher priority than visibilityByType
      const isTransparentForGuid = guidToTransparent[guid]
      if (isTransparentForGuid !== undefined) {
        acc[guid] = isTransparentForGuid ? 'transparent' : 'notTransparent'
        return acc
      }

      // Fallback to type-based transparency
      const isTransparentForType = transparencyVisibilityByType[elementType]
      acc[guid] = isTransparentForType ? 'transparent' : 'notTransparent'

      return acc
    }, {} as Record<string, OpacityOption>)
  }, [lintels, persistedRips, translucent, transparencyVisibilityByType, guidToTransparent])

  const guidToRenderMode = useMemo<Record<string, LineShapesRenderMode>>(() => {
    // All elements should render both body and outlines
    return [...lintels, ...persistedRips].reduce((acc, element) => {
      acc[element.position_guid] = {
        bodyGeometry: true,
        outline: true,
      }
      return acc
    }, {} as Record<string, LineShapesRenderMode>)
  }, [lintels, persistedRips])

  const selectedCheckPositions = useModelMeshStore(state => state.selectedCheckPositions)

  const { scenePalette } = useTheme()

  // Convert lintels to line elements
  const lintelElements = useMemo(() => {
    return lintels.map(lintel => {
      const crossSection = guidToCrossSection[lintel.position_guid] as CrossSection | undefined
      const height = crossSection?.shape?.height || DEFAULT_CROSS_SECTION_SIZE
      const width = crossSection?.shape?.width || DEFAULT_CROSS_SECTION_SIZE

      return {
        id: lintel.position_guid,
        start: [lintel.start.x, lintel.start.y, lintel.start.z] as [number, number, number],
        end: [lintel.end.x, lintel.end.y, lintel.end.z] as [number, number, number],
        coordinate_system: lintel.coordinate_system,
        height,
        width,
        isSteel: crossSection ? crossSectionIsSteel(crossSection) : false,
        crossSection,
      }
    })
  }, [lintels, guidToCrossSection])

  const nonSteelLintelElements = useMemo(
    () => filter(lintelElements, lintel => !lintel.isSteel),
    [lintelElements],
  )

  const steelLintelElements = useMemo(
    () => filter(lintelElements, lintel => lintel.isSteel),
    [lintelElements],
  )

  // Convert rips to line elements
  const ripElements = useMemo(() => {
    return persistedRips.map(rip => {
      const crossSection: CrossSection | undefined = guidToCrossSection[rip.position_guid]
      const height = crossSection?.shape?.height || DEFAULT_CROSS_SECTION_SIZE
      const width = crossSection?.shape?.width || DEFAULT_CROSS_SECTION_SIZE

      return {
        id: rip.position_guid,
        start: [rip.start.x, rip.start.y, rip.start.z] as [number, number, number],
        end: [rip.end.x, rip.end.y, rip.end.z] as [number, number, number],
        coordinate_system: rip.coordinate_system,
        height,
        width,
        isSteel: crossSection ? crossSectionIsSteel(crossSection) : false,
        crossSection,
      }
    })
  }, [persistedRips, guidToCrossSection])

  const nonSteelRipElements = useMemo(() => filter(ripElements, rip => !rip.isSteel), [ripElements])

  const steelRipElements = useMemo(() => filter(ripElements, rip => rip.isSteel), [ripElements])

  const hiddenGuids = useModelMeshStore(state => state.hiddenGuids)
  const selectedGuids = useModelMeshStore(state => state.selectedGuids)
  const highlightedPositions = useModelMeshStore(state => state.highlightedPositions)
  const guidToColor = useSceneStore(state => state.guidToColor)
  const targetPositions = useModelMeshStore(state => state.targetPositions)

  const allSelectedGuids = useMemo(() => {
    return Array.from(selectedGuids.values()).flat()
  }, [selectedGuids])

  const allHighlightedPositions = useMemo(() => {
    return Array.from(highlightedPositions.values()).flat()
  }, [highlightedPositions])

  const allTargetPositions = useMemo(() => {
    return Array.from(targetPositions.values()).flat()
  }, [targetPositions])

  // Color mapping for lintels - all use columns color
  const lintelGuidToColor = useMemo(() => {
    return lintelElements.reduce((acc, lintel) => {
      if (guidToColor[lintel.id]) {
        acc[lintel.id] = guidToColor[lintel.id] as string
      } else if (allHighlightedPositions.includes(lintel.id)) {
        acc[lintel.id] = scenePalette.selection as string
      } else if (lintel.isSteel) {
        acc[lintel.id] = scenePalette.elements3d.steelBeams as string
      } else if (allTargetPositions.includes(lintel.id)) {
        acc[lintel.id] = scenePalette.elements3d.targets as string
      } else {
        acc[lintel.id] = scenePalette.elements3d.columns as string
      }
      return acc
    }, {} as Record<string, string>)
  }, [
    lintelElements,
    guidToColor,
    allHighlightedPositions,
    allTargetPositions,
    scenePalette.selection,
    scenePalette.elements3d.steelBeams,
    scenePalette.elements3d.targets,
    scenePalette.elements3d.columns,
  ])

  // Color mapping for rips - all use columns color
  const ripGuidToColor = useMemo(() => {
    return ripElements.reduce((acc, rip) => {
      if (guidToColor[rip.id]) {
        acc[rip.id] = guidToColor[rip.id] as string
      } else if (allHighlightedPositions.includes(rip.id)) {
        acc[rip.id] = scenePalette.selection as string
      } else if (rip.isSteel) {
        acc[rip.id] = scenePalette.elements3d.steelBeams as string
      } else if (allTargetPositions.includes(rip.id)) {
        acc[rip.id] = scenePalette.elements3d.targets as string
      } else {
        acc[rip.id] = scenePalette.elements3d.columns as string
      }

      return acc
    }, {} as Record<string, string>)
  }, [
    ripElements,
    guidToColor,
    allHighlightedPositions,
    allTargetPositions,
    scenePalette.selection,
    scenePalette.elements3d.steelBeams,
    scenePalette.elements3d.targets,
    scenePalette.elements3d.columns,
  ])

  // Outlines color mapping for lintels - all white
  const lintelGuidToOutlinesColor = useMemo(() => {
    return lintels.reduce((acc, lintel) => {
      if (allSelectedGuids.includes(lintel.position_guid)) {
        acc[lintel.position_guid] = scenePalette.highlightDark as string
      } else {
        acc[lintel.position_guid] = scenePalette.outlines as string
      }
      return acc
    }, {} as Record<string, string>)
  }, [allSelectedGuids, lintels, scenePalette.highlightDark, scenePalette.outlines])

  // Outlines color mapping for rips - all white
  const ripGuidToOutlinesColor = useMemo(() => {
    return persistedRips.reduce((acc, rip) => {
      if (allSelectedGuids.includes(rip.position_guid)) {
        acc[rip.position_guid] = scenePalette.highlightDark as string
      } else {
        acc[rip.position_guid] = scenePalette.outlines as string
      }
      return acc
    }, {} as Record<string, string>)
  }, [allSelectedGuids, persistedRips, scenePalette.highlightDark, scenePalette.outlines])

  const positionVisibilityLookup = usePositionVisibilityLookup()

  const guidToHidden = useMemo(() => {
    const allGuids = [
      ...lintels.map(lintel => lintel.position_guid),
      ...persistedRips.map(rip => rip.position_guid),
    ]
    const allHiddenGuids = new Set([...Array.from(hiddenGuids.values()).flat()])

    return allGuids.reduce((acc, guid) => {
      acc[guid] = allHiddenGuids.has(guid) || !positionVisibilityLookup[guid]
      return acc
    }, {} as Record<string, boolean>)
  }, [lintels, persistedRips, hiddenGuids, positionVisibilityLookup])

  // No-op handlers for LineShapesMesh
  const noOpHandler = () => {}

  const executeListener = useEmitModelClickListenerV2()

  const handleClick = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => {
      executeListener(guid, event, { type: 'position' })
    },
    [executeListener],
  )

  const allSelectedCheckPositions = useMemo(
    () => Array.from(selectedCheckPositions.values()),
    [selectedCheckPositions],
  )

  const selectedPositions = useMemo(() => {
    const cylinderConfigs: {
      positionGuid: string
      positionVector: ImmutableVector3
    }[] = []
    for (const position of allSelectedCheckPositions) {
      if (!allSelectedGuids.includes(position.positionGuid)) continue

      const lintel = lintelElements.find(element => element.id === position.positionGuid)
      if (lintel) {
        cylinderConfigs.push({
          positionGuid: position.positionGuid,
          positionVector: toImmutable(
            getPositionVectorFromCheckPosition(
              position.checkPosition,
              toVector3(lintel.start),
              toVector3(lintel.end),
              'lintel',
            ),
          ),
        })
      }

      const rip = ripElements.find(element => element.id === position.positionGuid)
      if (rip) {
        cylinderConfigs.push({
          positionGuid: position.positionGuid,
          positionVector: toImmutable(
            getPositionVectorFromCheckPosition(
              position.checkPosition,
              toVector3(rip.start),
              toVector3(rip.end),
              'rip',
            ),
          ),
        })
      }
    }
    return cylinderConfigs
  }, [allSelectedCheckPositions, allSelectedGuids, lintelElements, ripElements])

  const { openContextMenu } = useTriggerContextMenu()
  const { mouseMoved } = useMouseMovedSinceMouseDown()

  const handleContextMenu = useCallback(
    (element: string, event: ThreeEvent<MouseEvent>) => {
      if (mouseMoved.current) return

      event.stopPropagation()

      openContextMenu(
        event.point,
        element,
        {
          hide: {
            guid: element,
          },
          properties: {
            guid: element,
          },
        },
        {},
      )
    },
    [mouseMoved, openContextMenu],
  )

  const handleContextMenuSteel = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      handleContextMenu(event.object.name, event)
    },
    [handleContextMenu],
  )

  return (
    <group>
      <LineShapesMesh
        elements={nonSteelLintelElements}
        guidToColor={lintelGuidToColor}
        guidToOutlinesColor={lintelGuidToOutlinesColor}
        guidToHidden={guidToHidden}
        guidToOpacity={guidToOpacity}
        opacityOptionValues={opacityOptionValues}
        guidToRenderMode={guidToRenderMode}
        renderOrder={100}
        handlePointerOver={noOpHandler}
        handlePointerOut={noOpHandler}
        handleClick={handleClick}
        handleContextMenu={handleContextMenu}
      />
      <LineShapesMesh
        elements={nonSteelRipElements}
        guidToColor={ripGuidToColor}
        guidToOutlinesColor={ripGuidToOutlinesColor}
        guidToHidden={guidToHidden}
        guidToOpacity={guidToOpacity}
        opacityOptionValues={opacityOptionValues}
        guidToRenderMode={guidToRenderMode}
        renderOrder={100}
        handlePointerOver={noOpHandler}
        handlePointerOut={noOpHandler}
        handleClick={handleClick}
        handleContextMenu={handleContextMenu}
      />
      {steelLintelElements.map(lintel => (
        <MemberMeshElement
          key={lintel.id}
          name={lintel.id}
          start={toImmutable(toVector3(lintel.start))}
          end={toImmutable(toVector3(lintel.end))}
          coordinateSystem={lintel.coordinate_system}
          userData={{
            guid: lintel.id,
            elementType: 'lintels',
            snapGeometry: 'centerLine',
            start: toImmutable(toVector3(lintel.start)),
            end: toImmutable(toVector3(lintel.end)),
          }}
          onClick={(event: ThreeEvent<MouseEvent>) => handleClick(lintel.id, event)}
          color={lintelGuidToColor[lintel.id]}
          outlinesColor={lintelGuidToOutlinesColor[lintel.id]}
          lineWidth={1}
          height={lintel.height}
          width={lintel.width}
          visible={!guidToHidden[lintel.id]}
          outlines={true}
          crossSectionShape={lintel.crossSection?.shape}
          opacity={guidToOpacity[lintel.id] === 'transparent' ? transparentOpacity : 1}
          onContextMenu={handleContextMenuSteel}
        />
      ))}
      {steelRipElements.map(rip => (
        <MemberMeshElement
          key={rip.id}
          name={rip.id}
          start={toImmutable(toVector3(rip.start))}
          end={toImmutable(toVector3(rip.end))}
          coordinateSystem={rip.coordinate_system}
          userData={{
            guid: rip.id,
            elementType: 'rips',
            snapGeometry: 'centerLine',
            start: toImmutable(toVector3(rip.start)),
            end: toImmutable(toVector3(rip.end)),
          }}
          onClick={(event: ThreeEvent<MouseEvent>) => handleClick(rip.id, event)}
          color={ripGuidToColor[rip.id]}
          outlinesColor={ripGuidToOutlinesColor[rip.id]}
          lineWidth={1}
          height={rip.height}
          width={rip.width}
          visible={!guidToHidden[rip.id]}
          outlines={true}
          crossSectionShape={rip.crossSection?.shape}
          opacity={guidToOpacity[rip.id] === 'transparent' ? transparentOpacity : 1}
          onContextMenu={handleContextMenuSteel}
        />
      ))}
      {selectedPositions.map(position => (
        <SimpleCylinderMesh
          key={position.positionGuid}
          position={position.positionVector}
          isActive={true}
          color="red"
          maxScale={7}
          disableDepthTest={true}
        />
      ))}
    </group>
  )
}

export default PositionsMesh
